import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaClock,
  FaArrowDown,
  FaArrowUp,
  FaChartBar,
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaBalanceScale,
} from "react-icons/fa";
import "./ProfitLoss.css";
import { FinancialStatementSkeleton } from "../../Component/Skeleton";
import SearchableSelect from "../../Component/SearchableSelect/SearchableSelect";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

const round2 = (val) => Math.round((Number(val) || 0) * 100) / 100;

// Indian Rupee currency formatter
const formatINR = (val) => {
  const num = Number(val || 0);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayDate = (dStr) => {
  if (!dStr) return "-";
  const [y, m, d] = String(dStr).split("-");
  if (!y || !m || !d) return dStr;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const mIndex = parseInt(m, 10) - 1;
  return `${d}-${months[mIndex] || m}-${y}`;
};

const DEFAULT_GROUP_NAMES = {
  10: "Revenue Accounts",
  14: "Purchase Account",
  19: "Direct Expenses",
  15: "Sales Account",
  13: "Direct Income",
  20: "Indirect Expenses",
  31: "Other Expenses",
  12: "Indirect Income",
};

/**
 * Extract group rows for display in Profit & Loss statement.
 * Shows ONLY AND ONLY group names with their total amounts.
 * Returns: [{ type: 'group', name, amount, groupCode, ledgers, subGroups }]
 */
const flattenGroupRows = (groups) => {
  const rows = [];
  (groups || []).forEach((g) => {
    if (g.total > 0.001) {
      rows.push({
        type: "group",
        name: g.groupName || DEFAULT_GROUP_NAMES[g.groupCode] || `Group ${g.groupCode}`,
        amount: round2(g.total) || 0,
        groupCode: g.groupCode,
        ledgers: g.ledgers || [],
        subGroups: g.subGroups || [],
      });
    }
  });
  return rows;
};

export default function ProfitLoss() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name:
        selectedCompany?.Name ||
        selectedCompany?.name ||
        selectedCompany?.CompanyName ||
        "DAZY TOURE TRAVEL'S",
      address:
        selectedCompany?.Address ||
        selectedCompany?.address ||
        "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  // Current financial year dates
  const defaultDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const fyStart = m >= 4 ? y : y - 1;
    const dStr = String(now.getDate()).padStart(2, "0");
    const mStr = String(now.getMonth() + 1).padStart(2, "0");
    return {
      fromDate: `${fyStart}-04-01`,
      toDate: `${y}-${mStr}-${dStr}`,
    };
  }, []);

  // Saved filter state from sessionStorage (preserves state on backward navigation)
  const savedState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("pl_saved_state");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const [fromDate, setFromDate] = useState(() => savedState?.fromDate || defaultDates.fromDate);
  const [toDate, setToDate] = useState(() => savedState?.toDate || defaultDates.toDate);
  const [selectedLedger, setSelectedLedger] = useState(() => savedState?.selectedLedger || "all");
  const [searchTerm, setSearchTerm] = useState(() => savedState?.searchTerm || "");

  // Persist filter state into sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "pl_saved_state",
      JSON.stringify({
        fromDate,
        toDate,
        selectedLedger,
        searchTerm,
      })
    );
  }, [fromDate, toDate, selectedLedger, searchTerm]);

  // Live timestamp for "As on" badge
  const [liveTimestamp, setLiveTimestamp] = useState("");

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const d = String(now.getDate()).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const m = months[now.getMonth()];
      const y = now.getFullYear();
      let hours = now.getHours();
      const mins = String(now.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      setLiveTimestamp(`${d}-${m}-${y} ${hours}:${mins} ${ampm}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Profit & Loss from API using React Query (cached in memory, prevents reset on back)
  const {
    data: apiData,
    isLoading: loading,
    refetch: fetchProfitLoss,
  } = useQuery({
    queryKey: ["profitLoss", companyId, fromDate, toDate],
    queryFn: async () => {
      if (!companyId) return null;
      try {
        const res = await axios.get(`${BASE_API_URL}/reports/profit-loss/${companyId}`, {
          params: { fromDate, toDate },
        });
        if (res.data?.success && res.data.data) {
          return res.data.data;
        }
        return null;
      } catch (err) {
        console.error("Failed to fetch P&L:", err);
        showToast.error("Failed to load Profit & Loss data");
        throw err;
      }
    },
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const handleApplyFilter = () => {
    fetchProfitLoss();
  };

  const handleReset = () => {
    sessionStorage.removeItem("pl_saved_state");
    setFromDate(defaultDates.fromDate);
    setToDate(defaultDates.toDate);
    setSelectedLedger("all");
    setSearchTerm("");
  };

  // Navigate to Group Ledger Summary when a group row is clicked
  const handleGroupClick = (groupCode, groupName) => {
    if (!groupCode) return;
    navigate(
      `/group-ledger-summary?groupCode=${groupCode}&groupName=${encodeURIComponent(
        groupName || ""
      )}&asOnDate=${toDate}`
    );
  };

  // =========================================================================
  // Derived display data from API response
  // =========================================================================
  const displayData = useMemo(() => {
    if (!apiData) {
      return {
        // Trading
        tradingExpenseRows: [],
        tradingIncomeRows: [],
        totalTradingExpenses: 0,
        totalTradingIncome: 0,
        grossProfit: 0,
        grossLoss: 0,
        isGrossProfit: true,
        tradingBalancedTotal: 0,
        // P&L
        plExpenseRows: [],
        plIncomeRows: [],
        totalPLExpenses: 0,
        totalPLIncome: 0,
        grossProfitCarried: 0,
        grossLossCarried: 0,
        netProfit: 0,
        netLoss: 0,
        isNetProfit: true,
        plBalancedTotal: 0,
        // Overall
        grandTotalExpenses: 0,
        grandTotalIncome: 0,
      };
    }

    const { trading, profitAndLoss } = apiData;

    // Trading rows
    let tradingExpenseRows = flattenGroupRows(trading?.expenses?.groups);
    let tradingIncomeRows = flattenGroupRows(trading?.income?.groups);

    // P&L rows
    let plExpenseRows = flattenGroupRows(profitAndLoss?.expenses?.groups);
    let plIncomeRows = flattenGroupRows(profitAndLoss?.income?.groups);

    // Filter group rows by selected ledger or search term
    const term = searchTerm.trim().toLowerCase();
    const filterGroupRows = (groupRows) => {
      if (selectedLedger === "all" && !term) return groupRows;

      const filtered = [];
      groupRows.forEach((g) => {
        // Collect all ledgers in this group
        const groupLedgers = [
          ...(g.ledgers || []),
          ...(g.subGroups || []).flatMap((sg) => sg.ledgers || []),
        ];

        // Filter ledgers by selectedLedger and term
        const matchingLedgers = groupLedgers.filter((l) => {
          const matchesLedger =
            selectedLedger === "all" || String(l.headCode) === String(selectedLedger);
          const matchesTerm =
            !term ||
            (l.headName && l.headName.toLowerCase().includes(term)) ||
            (g.name && g.name.toLowerCase().includes(term));
          return matchesLedger && matchesTerm;
        });

        if (matchingLedgers.length > 0) {
          const filteredAmt = round2(
            matchingLedgers.reduce((sum, l) => sum + (Number(l.closingBalance) || 0), 0)
          );
          if (filteredAmt > 0.001) {
            filtered.push({
              type: "group",
              name: g.name,
              amount: filteredAmt,
              groupCode: g.groupCode,
              ledgers: matchingLedgers,
            });
          }
        }
      });
      return filtered;
    };

    tradingExpenseRows = filterGroupRows(tradingExpenseRows);
    tradingIncomeRows = filterGroupRows(tradingIncomeRows);
    plExpenseRows = filterGroupRows(plExpenseRows);
    plIncomeRows = filterGroupRows(plIncomeRows);

    // If a specific ledger is selected and has 0 balance (not present in normal non-zero rows)
    if (selectedLedger !== "all") {
      const alreadyInTable =
        tradingExpenseRows.length > 0 ||
        tradingIncomeRows.length > 0 ||
        plExpenseRows.length > 0 ||
        plIncomeRows.length > 0;

      if (!alreadyInTable) {
        const targetLedger = (apiData.allLedgers || []).find(
          (l) => String(l.headCode) === String(selectedLedger)
        );
        if (targetLedger) {
          const cat = targetLedger.plCategory;
          const grpName =
            DEFAULT_GROUP_NAMES[cat] || targetLedger.groupName || `Group ${cat}`;
          const rowItem = {
            type: "group",
            name: grpName,
            amount: 0,
            groupCode: cat,
            ledgers: [targetLedger],
          };
          if ([10, 14, 19].includes(cat)) {
            tradingExpenseRows = [rowItem];
          } else if ([15, 13].includes(cat)) {
            tradingIncomeRows = [rowItem];
          } else if ([20, 31].includes(cat)) {
            plExpenseRows = [rowItem];
          } else if ([12].includes(cat)) {
            plIncomeRows = [rowItem];
          }
        }
      }
    }

    const isFiltered = selectedLedger !== "all" || Boolean(term);

    const totalTradingExpenses = isFiltered
      ? round2(tradingExpenseRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0))
      : (trading?.expenses?.totalExpenses || 0);
    const totalTradingIncome = isFiltered
      ? round2(tradingIncomeRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0))
      : (trading?.income?.totalIncome || 0);

    const grossDiff = round2(totalTradingIncome - totalTradingExpenses);
    const isGrossProfit = grossDiff >= 0;
    const grossProfit = isGrossProfit ? grossDiff : 0;
    const grossLoss = !isGrossProfit ? Math.abs(grossDiff) : 0;
    const tradingBalancedTotal = round2(
      Math.max(totalTradingExpenses + grossProfit, totalTradingIncome + grossLoss)
    );

    const totalPLExpenses = isFiltered
      ? round2(plExpenseRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0))
      : (profitAndLoss?.expenses?.totalExpenses || 0);
    const totalPLIncome = isFiltered
      ? round2(plIncomeRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0))
      : (profitAndLoss?.income?.totalIncome || 0);

    const grossProfitCarried = grossProfit;
    const grossLossCarried = grossLoss;

    const creditSideTotal = round2(totalPLIncome + grossProfitCarried);
    const debitSideTotal = round2(totalPLExpenses + grossLossCarried);
    const netDiff = round2(creditSideTotal - debitSideTotal);
    const isNetProfit = netDiff >= 0;
    const netProfit = isNetProfit ? netDiff : 0;
    const netLoss = !isNetProfit ? Math.abs(netDiff) : 0;
    const plBalancedTotal = round2(
      Math.max(
        debitSideTotal + (isNetProfit ? netProfit : 0),
        creditSideTotal + (!isNetProfit ? netLoss : 0)
      )
    );

    const grandTotalExpenses = round2(totalTradingExpenses + totalPLExpenses);
    const grandTotalIncome = round2(totalTradingIncome + totalPLIncome);

    return {
      tradingExpenseRows,
      tradingIncomeRows,
      totalTradingExpenses,
      totalTradingIncome,
      grossProfit,
      grossLoss,
      isGrossProfit,
      tradingBalancedTotal,
      plExpenseRows,
      plIncomeRows,
      totalPLExpenses,
      totalPLIncome,
      grossProfitCarried,
      grossLossCarried,
      netProfit,
      netLoss,
      isNetProfit,
      plBalancedTotal,
      grandTotalExpenses,
      grandTotalIncome,
    };
  }, [apiData, searchTerm, selectedLedger]);

  // Spacer counts to equally balance Expenses and Income tables
  const tradingExpRowsCount = (displayData.tradingExpenseRows || []).length + (displayData.isGrossProfit && displayData.grossProfit > 0 ? 1 : 0);
  const tradingIncRowsCount = (displayData.tradingIncomeRows || []).length + (!displayData.isGrossProfit && displayData.grossLoss > 0 ? 1 : 0);
  const tradingExpSpacerCount = Math.max(0, tradingIncRowsCount - tradingExpRowsCount);
  const tradingIncSpacerCount = Math.max(0, tradingExpRowsCount - tradingIncRowsCount);

  const plExpRowsCount = (displayData.plExpenseRows || []).length + (displayData.isNetProfit && displayData.netProfit > 0 ? 1 : 0);
  const plIncRowsCount = (displayData.plIncomeRows || []).length + (displayData.grossProfitCarried > 0 ? 1 : 0) + (!displayData.isNetProfit && displayData.netLoss > 0 ? 1 : 0);
  const plExpSpacerCount = Math.max(0, plIncRowsCount - plExpRowsCount);
  const plIncSpacerCount = Math.max(0, plExpRowsCount - plIncRowsCount);

  // Helper for Print and PDF rows alignment
  const buildPrintRows = (expRows, incRows) => {
    const maxLen = Math.max((expRows || []).length, (incRows || []).length);
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      const exp = expRows && expRows[i] ? expRows[i] : { name: "", amount: "" };
      const inc = incRows && incRows[i] ? incRows[i] : { name: "", amount: "" };
      rows.push({
        expName: exp.name || "",
        expAmt: exp.amount !== "" ? exp.amount : "",
        incName: inc.name || "",
        incAmt: inc.amount !== "" ? inc.amount : "",
      });
    }
    return rows;
  };

  // =========================================================================
  // Render a column's rows (group rows - clickable, navigates to Group Ledger Summary)
  // =========================================================================
  const renderRows = (rows, prefix, hasOtherItems = false) => {
    if (!rows || rows.length === 0) {
      if (hasOtherItems) return null;
      return (
        <tr>
          <td colSpan="2" style={{ textAlign: "center", padding: "14px", color: "#94a3b8", fontSize: "11px" }}>
            No data for this period.
          </td>
        </tr>
      );
    }
    return rows.map((row, idx) => {
      const isClickable = Boolean(row.groupCode);
      return (
        <tr
          key={`${prefix}-l-${idx}`}
          className={isClickable ? "pl_clickable_group_row" : ""}
          onClick={() => {
            if (isClickable) {
              handleGroupClick(row.groupCode, row.name);
            }
          }}
          title={isClickable ? `Click to view ${row.name} in Group Ledger Summary` : undefined}
        >
          <td
            className="pl_ledger_name"
            style={{
              paddingLeft: "12px",
              fontWeight: 600,
            }}
          >
            <span className={isClickable ? "pl_link_text" : ""}>{row.name}</span>
          </td>
          <td
            className="pl_ledger_amt"
            style={{
              fontWeight: 600,
              color: "#0f172a",
            }}
          >
            {formatINR(row.amount)}
          </td>
        </tr>
      );
    });
  };

  // =========================================================================
  // EXPORT TO EXCEL (T-Format: Trading + P&L)
  // =========================================================================
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "PROFIT & LOSS ACCOUNT";
      const periodSubtitle = `Period: ${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}  |  As on: ${liveTimestamp}`;

      const headers = [
        "Expenses",
        "Amount (INR)",
        "",
        "Income",
        "Amount (INR)",
      ];

      const buildExcelRows = (expenseRows, incomeRows) => {
        const expFlat = expenseRows.map((r) => ({
          name: r.type === "group" ? r.name : r.type === "subgroup" ? `  ${r.name}` : `    ${r.name}`,
          amt: r.amount,
          isBold: r.type !== "ledger",
        }));
        const incFlat = incomeRows.map((r) => ({
          name: r.type === "group" ? r.name : r.type === "subgroup" ? `  ${r.name}` : `    ${r.name}`,
          amt: r.amount,
          isBold: r.type !== "ledger",
        }));
        const maxLen = Math.max(expFlat.length, incFlat.length);
        const rows = [];
        for (let i = 0; i < maxLen; i++) {
          const exp = expFlat[i] || { name: "", amt: "" };
          const inc = incFlat[i] || { name: "", amt: "" };
          rows.push([
            exp.name,
            exp.amt !== "" ? Number(exp.amt) : "",
            "",
            inc.name,
            inc.amt !== "" ? Number(inc.amt) : "",
          ]);
        }
        return rows;
      };

      // Trading Account Section
      const tradingExpenseRowsForExcel = [
        ...displayData.tradingExpenseRows,
        ...(displayData.isGrossProfit && displayData.grossProfit > 0
          ? [{ type: "group", name: "Gross Profit", amount: displayData.grossProfit }]
          : []),
      ];
      const tradingIncomeRowsForExcel = [
        ...displayData.tradingIncomeRows,
        ...(!displayData.isGrossProfit && displayData.grossLoss > 0
          ? [{ type: "group", name: "Gross Loss", amount: displayData.grossLoss }]
          : []),
      ];
      const tradingRows = buildExcelRows(tradingExpenseRowsForExcel, tradingIncomeRowsForExcel);
      const tradingTotalRow = [
        "Total",
        displayData.tradingBalancedTotal,
        "",
        "Total",
        displayData.tradingBalancedTotal,
      ];

      // P&L Section
      const plExpenseRowsForExcel = [
        ...displayData.plExpenseRows,
        ...(displayData.grossLossCarried > 0
          ? [{ type: "ledger", name: "", amount: displayData.grossLossCarried }]
          : []),
        ...(displayData.isNetProfit && displayData.netProfit > 0
          ? [{ type: "group", name: "Net Profit", amount: displayData.netProfit }]
          : []),
      ];
      const plIncomeRowsForExcel = [
        ...(displayData.grossProfitCarried > 0
          ? [{ type: "ledger", name: "", amount: displayData.grossProfitCarried }]
          : []),
        ...displayData.plIncomeRows,
        ...(!displayData.isNetProfit && displayData.netLoss > 0
          ? [{ type: "group", name: "Net Loss", amount: displayData.netLoss }]
          : []),
      ];
      const plRows = buildExcelRows(plExpenseRowsForExcel, plIncomeRowsForExcel);
      const plTotalRow = [
        "Total",
        displayData.plBalancedTotal,
        "",
        "Total",
        displayData.plBalancedTotal,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...tradingRows,
        tradingTotalRow,
        [],
        ...plRows,
        plTotalRow,
        [],
        ["Note: Figures are in Indian Rupees (INR)."],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
      ];
      worksheet["!cols"] = [
        { wch: 36 },
        { wch: 18 },
        { wch: 4 },
        { wch: 36 },
        { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Profit_and_Loss");
      XLSX.writeFile(workbook, `Profit_and_Loss_${fromDate}_to_${toDate}.xlsx`);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error("Failed to export Excel:", err);
      showToast.error("Failed to export Excel");
    }
  };

  // =========================================================================
  // EXPORT TO PDF
  // =========================================================================
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "PROFIT & LOSS ACCOUNT";

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Period: ${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`, 14, 36);
      doc.text(`As on: ${liveTimestamp}`, pageWidth - 14, 36, { align: "right" });

      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      // Build table rows (Trading + P&L combined)
      const buildPdfRows = (expRows, incRows) => {
        const eFmt = expRows.map((r) => ({
          name: r.type === "group" ? r.name : r.type === "subgroup" ? `  ${r.name}` : `   ${r.name}`,
          amt: formatINR(r.amount),
          isBold: r.type !== "ledger",
        }));
        const iFmt = incRows.map((r) => ({
          name: r.type === "group" ? r.name : r.type === "subgroup" ? `  ${r.name}` : `   ${r.name}`,
          amt: formatINR(r.amount),
          isBold: r.type !== "ledger",
        }));
        const maxLen = Math.max(eFmt.length, iFmt.length);
        const rows = [];
        for (let i = 0; i < maxLen; i++) {
          const exp = eFmt[i] || { name: "", amt: "" };
          const inc = iFmt[i] || { name: "", amt: "" };
          rows.push([exp.name, exp.amt, inc.name, inc.amt]);
        }
        return rows;
      };

      const tableRows = [];

      const tradingExpPdf = [
        ...displayData.tradingExpenseRows,
        ...(displayData.isGrossProfit && displayData.grossProfit > 0
          ? [{ type: "group", name: "Gross Profit", amount: displayData.grossProfit }]
          : []),
      ];
      const tradingIncPdf = [
        ...displayData.tradingIncomeRows,
        ...(!displayData.isGrossProfit && displayData.grossLoss > 0
          ? [{ type: "group", name: "Gross Loss", amount: displayData.grossLoss }]
          : []),
      ];
      tableRows.push(...buildPdfRows(tradingExpPdf, tradingIncPdf));
      tableRows.push([
        "Total",
        formatINR(displayData.tradingBalancedTotal),
        "Total",
        formatINR(displayData.tradingBalancedTotal),
      ]);

      // Separator
      tableRows.push(["", "", "", ""]);

      const plExpPdf = [
        ...displayData.plExpenseRows,
        ...(displayData.grossLossCarried > 0
          ? [{ type: "ledger", name: "", amount: displayData.grossLossCarried }]
          : []),
        ...(displayData.isNetProfit && displayData.netProfit > 0
          ? [{ type: "group", name: "Net Profit", amount: displayData.netProfit }]
          : []),
      ];
      const plIncPdf = [
        ...(displayData.grossProfitCarried > 0
          ? [{ type: "ledger", name: "", amount: displayData.grossProfitCarried }]
          : []),
        ...displayData.plIncomeRows,
        ...(!displayData.isNetProfit && displayData.netLoss > 0
          ? [{ type: "group", name: "Net Loss", amount: displayData.netLoss }]
          : []),
      ];
      tableRows.push(...buildPdfRows(plExpPdf, plIncPdf));
      tableRows.push([
        "Total",
        formatINR(displayData.plBalancedTotal),
        "Total",
        formatINR(displayData.plBalancedTotal),
      ]);

      autoTable(doc, {
        head: [
          [
            { content: "Expenses", styles: { halign: "left" } },
            { content: "Amount (INR)", styles: { halign: "right" } },
            { content: "Income", styles: { halign: "left" } },
            { content: "Amount (INR)", styles: { halign: "right" } },
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [30, 41, 59],
          cellPadding: 2,
        },
        columnStyles: {
          0: { cellWidth: 62, halign: "left" },
          1: { cellWidth: 30, halign: "right" },
          2: { cellWidth: 62, halign: "left" },
          3: { cellWidth: 30, halign: "right" },
        },
        didParseCell: (data) => {
          const rawRow = data.row.raw || [];
          const firstCol = String(rawRow[0] || "");
          const thirdCol = String(rawRow[2] || "");

          // Total rows
          if (firstCol === "Total") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [248, 250, 252];
            data.cell.styles.textColor = [15, 23, 42];
          }

          // Loss highlights (red)
          if (firstCol === "Gross Loss" || thirdCol === "Gross Loss") {
            data.cell.styles.textColor = [153, 27, 27];
          }
          if (firstCol === "Net Loss" || thirdCol === "Net Loss") {
            data.cell.styles.textColor = [153, 27, 27];
            data.cell.styles.fontStyle = "bold";
          }

          // Profit highlights (green)
          if (firstCol === "Gross Profit" || thirdCol === "Gross Profit") {
            data.cell.styles.textColor = [22, 101, 52];
          }
          if (firstCol === "Net Profit" || thirdCol === "Net Profit") {
            data.cell.styles.textColor = [22, 101, 52];
            data.cell.styles.fontStyle = "bold";
          }
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            "etour - Design & Develop by Response Infoway",
            14,
            doc.internal.pageSize.getHeight() - 8
          );
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageWidth - 14,
            doc.internal.pageSize.getHeight() - 8,
            { align: "right" }
          );
        },
        margin: { left: 14, right: 14, bottom: 15 },
      });

      doc.save(`Profit_and_Loss_${fromDate}_to_${toDate}.pdf`);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error("Failed to export PDF:", err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="pl_page_wrapper">
      {/* Top Header & Breadcrumb */}
      <div className="pl_header_section">
        <div className="pl_breadcrumb_bar">
          Reports <span>&gt;</span> <strong>Profit & Loss Account</strong>
        </div>
        <div className="pl_as_on_badge">
          <FaClock />
          As on: {liveTimestamp}
        </div>
      </div>

      {/* Filter Card */}
      <div className="pl_filter_card">
        <div className="pl_filter_bar">
          {/* From Date */}
          <div className="pl_filter_group">
            <label className="pl_filter_label">
              From Date<span className="required_star">*</span>:
            </label>
            <input
              type="date"
              className="pl_filter_input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: 130 }}
            />
          </div>

          {/* To Date */}
          <div className="pl_filter_group">
            <label className="pl_filter_label">
              To Date<span className="required_star">*</span>:
            </label>
            <input
              type="date"
              className="pl_filter_input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: 130 }}
            />
          </div>

          {/* Ledger Filter */}
          <div className="pl_filter_group">
            <label className="pl_filter_label">Ledger:</label>
            <SearchableSelect
              options={[
                { value: "all", label: "-- All Ledgers --" },
                ...(apiData?.ledgerDropdown || []).map((l) => ({
                  value: l.headCode,
                  label: l.headName,
                })),
              ]}
              value={selectedLedger}
              onChange={(val) => setSelectedLedger(val || "all")}
              placeholder="-- All Ledgers --"
              width="220px"
              height="30px"
            />
          </div>

          {/* Action Buttons */}
          <div className="pl_filter_buttons">
            <button
              type="button"
              className="pl_btn_apply"
              onClick={handleApplyFilter}
              disabled={loading}
              title="Apply filters"
            >
              View
            </button>
            <button
              type="button"
              className="pl_btn_reset"
              onClick={handleReset}
              title="Reset filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards - 4 cards */}
      <div className="pl_kpi_row pl_kpi_row_4">
        {/* Card 1: Total Expenses */}
        <div className="pl_kpi_card">
          <div className="pl_kpi_icon_wrapper pl_kpi_icon_expense">
            <FaArrowDown />
          </div>
          <div className="pl_kpi_content">
            <span className="pl_kpi_title">Total Expenses</span>
            <span className="pl_kpi_value">
              ₹ {formatINR(displayData.grandTotalExpenses)}
            </span>
          </div>
        </div>

        {/* Card 2: Total Income */}
        <div className="pl_kpi_card">
          <div className="pl_kpi_icon_wrapper pl_kpi_icon_income">
            <FaArrowUp />
          </div>
          <div className="pl_kpi_content">
            <span className="pl_kpi_title">Total Income</span>
            <span className="pl_kpi_value">
              ₹ {formatINR(displayData.grandTotalIncome)}
            </span>
          </div>
        </div>

        {/* Card 3: Gross Profit/Loss */}
        <div className="pl_kpi_card">
          <div className="pl_kpi_icon_wrapper pl_kpi_icon_gross">
            <FaBalanceScale />
          </div>
          <div className="pl_kpi_content">
            <span className="pl_kpi_title">
              {displayData.isGrossProfit ? "Gross Profit" : "Gross Loss"}
            </span>
            <span
              className={`pl_kpi_value ${
                displayData.isGrossProfit ? "pl_kpi_value_profit" : "pl_kpi_value_loss"
              }`}
            >
              ₹ {formatINR(displayData.isGrossProfit ? displayData.grossProfit : displayData.grossLoss)}
            </span>
          </div>
        </div>

        {/* Card 4: Net Profit / Loss */}
        <div className="pl_kpi_card">
          <div className="pl_kpi_icon_wrapper pl_kpi_icon_profit">
            <FaChartBar />
          </div>
          <div className="pl_kpi_content">
            <span className="pl_kpi_title">
              {displayData.isNetProfit ? "Net Profit" : "Net Loss"}
            </span>
            <span
              className={`pl_kpi_value ${
                displayData.isNetProfit ? "pl_kpi_value_profit" : "pl_kpi_value_loss"
              }`}
            >
              ₹ {formatINR(displayData.isNetProfit ? displayData.netProfit : displayData.netLoss)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="pl_table_card">
        {/* Table Toolbar */}
        <div className="pl_table_toolbar">
          <h3 className="pl_table_title">
            Statement of Profit & Loss ({formatDisplayDate(fromDate)} to {formatDisplayDate(toDate)})
          </h3>

          <div className="pl_toolbar_right">
            {/* Quick Search */}
            <div className="pl_search_box">
              <input
                type="text"
                className="pl_search_input"
                placeholder="Search ledger or group..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="pl_search_icon" />
            </div>

            {/* Action Buttons */}
            <button
              type="button"
              className="pl_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Microsoft Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>
            <button
              type="button"
              className="pl_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF document"
            >
              <FaFilePdf />
              Export PDF
            </button>
            <button
              type="button"
              className="pl_export_btn btn_print"
              onClick={handlePrint}
              title="Print Statement"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {loading ? (
          <FinancialStatementSkeleton title="Profit & Loss" />
        ) : !apiData ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
            No data available. Please check the date range and try again.
          </div>
        ) : (
          <>
            <div className="pl_statements_container">
                  {/* LEFT: Trading Expenses */}
                  <div className="pl_column_card">
                    <div className="pl_column_header pl_header_expense">
                      <span>Expenses</span>
                    </div>
                    <table className="pl_statement_table">
                      <thead>
                        <tr>
                          <th style={{ width: "65%" }}>Particulars</th>
                          <th style={{ width: "35%", textAlign: "right" }}>Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRows(
                          displayData.tradingExpenseRows,
                          "te",
                          displayData.isGrossProfit && displayData.grossProfit > 0
                        )}
                        {Array.from({ length: tradingExpSpacerCount }).map((_, idx) => (
                          <tr key={`te-sp-${idx}`} className="pl_spacer_row">
                            <td className="pl_ledger_name">&nbsp;</td>
                            <td className="pl_ledger_amt">&nbsp;</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {/* Gross Profit on expense side to balance */}
                        {displayData.isGrossProfit && displayData.grossProfit > 0 && (
                          <tr className="pl_gross_row">
                            <td>Gross Profit</td>
                            <td style={{ textAlign: "right" }}>{formatINR(displayData.grossProfit)}</td>
                          </tr>
                        )}
                        <tr className="pl_column_total_row">
                          <td>Total</td>
                          <td style={{ textAlign: "right" }}>
                            ₹ {formatINR(displayData.tradingBalancedTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* RIGHT: Trading Income */}
                  <div className="pl_column_card">
                    <div className="pl_column_header pl_header_income">
                      <span>Income</span>
                    </div>
                    <table className="pl_statement_table">
                      <thead>
                        <tr>
                          <th style={{ width: "65%" }}>Particulars</th>
                          <th style={{ width: "35%", textAlign: "right" }}>Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRows(
                          displayData.tradingIncomeRows,
                          "ti",
                          !displayData.isGrossProfit && displayData.grossLoss > 0
                        )}
                        {Array.from({ length: tradingIncSpacerCount }).map((_, idx) => (
                          <tr key={`ti-sp-${idx}`} className="pl_spacer_row">
                            <td className="pl_ledger_name">&nbsp;</td>
                            <td className="pl_ledger_amt">&nbsp;</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {/* Gross Loss on income side to balance */}
                        {!displayData.isGrossProfit && displayData.grossLoss > 0 && (
                          <tr className="pl_gross_row pl_gross_loss_row">
                            <td>Gross Loss</td>
                            <td style={{ textAlign: "right" }}>{formatINR(displayData.grossLoss)}</td>
                          </tr>
                        )}
                        <tr className="pl_column_total_row">
                          <td>Total</td>
                          <td style={{ textAlign: "right" }}>
                            ₹ {formatINR(displayData.tradingBalancedTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Gross Profit/Loss Banner */}
                <div
                  className={`pl_gross_banner ${
                    displayData.isGrossProfit ? "pl_banner_profit" : "pl_banner_loss"
                  }`}
                >
                  <div className="pl_banner_label">
                    {displayData.isGrossProfit ? "Gross Profit" : "Gross Loss"}
                  </div>
                  <div className="pl_banner_amount">
                    ₹ {formatINR(displayData.isGrossProfit ? displayData.grossProfit : displayData.grossLoss)}
                  </div>
                </div>

                <div className="pl_statements_container" style={{ marginTop: "12px" }}>
                  {/* LEFT: P&L Expenses */}
                  <div className="pl_column_card">
                    <div className="pl_column_header pl_header_expense">
                      <span>Expenses</span>
                    </div>
                    <table className="pl_statement_table">
                      <thead>
                        <tr>
                          <th style={{ width: "65%" }}>Particulars</th>
                          <th style={{ width: "35%", textAlign: "right" }}>Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRows(
                          displayData.plExpenseRows,
                          "pe",
                          displayData.isNetProfit && displayData.netProfit > 0
                        )}
                        {Array.from({ length: plExpSpacerCount }).map((_, idx) => (
                          <tr key={`pe-sp-${idx}`} className="pl_spacer_row">
                            <td className="pl_ledger_name">&nbsp;</td>
                            <td className="pl_ledger_amt">&nbsp;</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {/* Net Profit on expense side to balance */}
                        {displayData.isNetProfit && displayData.netProfit > 0 && (
                          <tr className="pl_gross_row">
                            <td>Net Profit</td>
                            <td style={{ textAlign: "right" }}>{formatINR(displayData.netProfit)}</td>
                          </tr>
                        )}
                        <tr className="pl_column_total_row">
                          <td>Total</td>
                          <td style={{ textAlign: "right" }}>
                            ₹ {formatINR(displayData.plBalancedTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* RIGHT: P&L Income */}
                  <div className="pl_column_card">
                    <div className="pl_column_header pl_header_income">
                      <span>Income</span>
                    </div>
                    <table className="pl_statement_table">
                      <thead>
                        <tr>
                          <th style={{ width: "65%" }}>Particulars</th>
                          <th style={{ width: "35%", textAlign: "right" }}>Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Gross Profit carried from Trading */}
                        {displayData.grossProfitCarried > 0 && (
                          <tr className="pl_carried_row">
                            <td className="pl_ledger_name"></td>
                            <td className="pl_ledger_amt" style={{ textAlign: "right", color: "#0f172a", fontWeight: 700 }}>
                              {formatINR(displayData.grossProfitCarried)}
                            </td>
                          </tr>
                        )}
                        {renderRows(
                          displayData.plIncomeRows,
                          "pi",
                          displayData.grossProfitCarried > 0 || (!displayData.isNetProfit && displayData.netLoss > 0)
                        )}
                        {Array.from({ length: plIncSpacerCount }).map((_, idx) => (
                          <tr key={`pi-sp-${idx}`} className="pl_spacer_row">
                            <td className="pl_ledger_name">&nbsp;</td>
                            <td className="pl_ledger_amt">&nbsp;</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {/* Net Loss on income side to balance */}
                        {!displayData.isNetProfit && displayData.netLoss > 0 && (
                          <tr className="pl_gross_row pl_gross_loss_row">
                            <td>Net Loss</td>
                            <td style={{ textAlign: "right" }}>{formatINR(displayData.netLoss)}</td>
                          </tr>
                        )}
                        <tr className="pl_column_total_row">
                          <td>Total</td>
                          <td style={{ textAlign: "right" }}>
                            ₹ {formatINR(displayData.plBalancedTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Net Profit/Loss Banner */}
                <div
                  className={`pl_net_profit_banner ${
                    displayData.isNetProfit ? "pl_banner_profit" : "pl_banner_loss"
                  }`}
                >
                  <div className="pl_banner_label">
                    {displayData.isNetProfit ? "★ Net Profit" : "★ Net Loss"}
                  </div>
                  <div className="pl_banner_amount">
                    ₹ {formatINR(displayData.isNetProfit ? displayData.netProfit : displayData.netLoss)}
                  </div>
                </div>
          </>
        )}


      </div>

      {/* DEDICATED PRINT CONTAINER (Active only during @media print, matching Monthly Ledger Summary) */}
      <div className="pl_print_area d-none">
        {/* Company Header */}
        <div style={{ textAlign: "center", borderBottom: "2px solid #000000", paddingBottom: "8px", marginBottom: "8px" }}>
          <h2 style={{ margin: "0 0 3px 0", fontSize: "19px", fontWeight: "bold", textTransform: "uppercase", color: "#000000" }}>
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px", fontWeight: "bold", color: "#000000", textDecoration: "underline" }}>
            PROFIT & LOSS ACCOUNT
          </div>
        </div>

        {/* Print Filter Meta */}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", borderBottom: "1px solid #000000", paddingBottom: "4px" }}>
          <div>
            <strong>Period:</strong> {formatDisplayDate(fromDate)} to {formatDisplayDate(toDate)}
            {selectedLedger !== "all" && (
              <span> | <strong>Filter:</strong> {apiData?.ledgerDropdown?.find((l) => String(l.headCode) === String(selectedLedger))?.headName || selectedLedger}</span>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {liveTimestamp}
          </div>
        </div>

        {/* Printable Table */}
        <table className="pl_print_table">
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Expenses</th>
              <th style={{ width: "15%", textAlign: "right" }}>Amount (₹)</th>
              <th style={{ width: "35%" }}>Income</th>
              <th style={{ width: "15%", textAlign: "right" }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {buildPrintRows(displayData.tradingExpenseRows, displayData.tradingIncomeRows).map((r, i) => (
              <tr key={`print-tr-${i}`} className={i % 2 === 1 ? "print_row_alt" : ""}>
                <td style={{ fontWeight: 600 }}>{r.expName}</td>
                <td style={{ textAlign: "right" }}>{r.expAmt !== "" ? formatINR(r.expAmt) : ""}</td>
                <td style={{ fontWeight: 600 }}>{r.incName}</td>
                <td style={{ textAlign: "right" }}>{r.incAmt !== "" ? formatINR(r.incAmt) : ""}</td>
              </tr>
            ))}
            <tr className="print_gross_row">
              <td style={{ fontWeight: 700 }}>{displayData.isGrossProfit ? "Gross Profit" : ""}</td>
              <td style={{ textAlign: "right", fontWeight: 700 }}>
                {displayData.isGrossProfit && displayData.grossProfit > 0 ? formatINR(displayData.grossProfit) : ""}
              </td>
              <td style={{ fontWeight: 700 }}>{!displayData.isGrossProfit ? "Gross Loss" : ""}</td>
              <td style={{ textAlign: "right", fontWeight: 700 }}>
                {!displayData.isGrossProfit && displayData.grossLoss > 0 ? formatINR(displayData.grossLoss) : ""}
              </td>
            </tr>
            <tr className="print_total_row">
              <td style={{ fontWeight: 800 }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: 800 }}>{formatINR(displayData.tradingBalancedTotal)}</td>
              <td style={{ fontWeight: 800 }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: 800 }}>{formatINR(displayData.tradingBalancedTotal)}</td>
            </tr>

            {buildPrintRows(displayData.plExpenseRows, displayData.plIncomeRows).map((r, i) => (
              <tr key={`print-pl-${i}`} className={i % 2 === 1 ? "print_row_alt" : ""}>
                <td style={{ fontWeight: 600 }}>{r.expName}</td>
                <td style={{ textAlign: "right" }}>{r.expAmt !== "" ? formatINR(r.expAmt) : ""}</td>
                <td style={{ fontWeight: 600 }}>{r.incName}</td>
                <td style={{ textAlign: "right" }}>{r.incAmt !== "" ? formatINR(r.incAmt) : ""}</td>
              </tr>
            ))}
            {(displayData.grossProfitCarried > 0 || displayData.grossLossCarried > 0) && (
              <tr>
                <td></td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {displayData.grossLossCarried > 0 ? formatINR(displayData.grossLossCarried) : ""}
                </td>
                <td style={{ fontWeight: 700 }}>{displayData.grossProfitCarried > 0 ? "Gross Profit" : ""}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>
                  {displayData.grossProfitCarried > 0 ? formatINR(displayData.grossProfitCarried) : ""}
                </td>
              </tr>
            )}
            <tr className="print_net_row">
              <td style={{ fontWeight: 700 }}>
                {displayData.isNetProfit && displayData.netProfit > 0 ? "Net Profit" : ""}
              </td>
              <td style={{ textAlign: "right", fontWeight: 700 }}>
                {displayData.isNetProfit && displayData.netProfit > 0 ? formatINR(displayData.netProfit) : ""}
              </td>
              <td style={{ fontWeight: 700 }}>
                {!displayData.isNetProfit && displayData.netLoss > 0 ? "Net Loss" : ""}
              </td>
              <td style={{ textAlign: "right", fontWeight: 700 }}>
                {!displayData.isNetProfit && displayData.netLoss > 0 ? formatINR(displayData.netLoss) : ""}
              </td>
            </tr>
            <tr className="print_total_row">
              <td style={{ fontWeight: 800 }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: 800 }}>{formatINR(displayData.plBalancedTotal)}</td>
              <td style={{ fontWeight: 800 }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: 800 }}>{formatINR(displayData.plBalancedTotal)}</td>
            </tr>
          </tbody>
        </table>

        {/* Footer */}
        <div className="pl_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {liveTimestamp}</span>
        </div>
      </div>
    </div>
  );
}
